// @ts-check

import { get_forms } from '~/api/zero-api';
import { TemplateActionCell, TemplateNameCell, TemplateTypeCell, TemplateTeamsCell, TemplateScheduleCell } from '~/components/Forms/TemplateDataGrid/TemplateDataGrid.cells';
import { AgGridFilter } from '~/other/agGridHelper';
import { memoize } from "lodash-es";
import { generateDataGridEventNames } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';
import { FormType } from '~/other/Constants';
import { dateFormatterNoTime, formatName } from '~/other/Helper';
import { SingleAvatarCell } from '~/components/Shared/DataGrid/SharedDataGridCells';

export const EventNames = generateDataGridEventNames('TemplateDataGrid');

export const DEFAULT_SORT_MODEL = [
    {
        colId: "name",
        sort: "asc",
    },
];

export function getSortByColumnName(colName) {
    switch (colName) {
        default: return colName;
    }
}

/**
 * @param {string} pageType
 * @param {any[]} categories
 * @returns {AgGrid.GridOptions['columnDefs']}
 */
export function getColumnDefs(pageType, categories) {
    /** @type {AgGrid.GridOptions['columnDefs']} */
    let defs = [
        {
            headerName: "",
            field: "_actions_",
            width: 50,
            cellRenderer: TemplateActionCell,
            resizable: false,
            sortable: false,
            suppressMovable: true,
            lockPosition: "left",
            cellStyle: {
                padding: 0,
            }
        },
        {
            headerName: pageType === "lms" ? "Course" : "Template",
            field: "name",
            initialSort: "asc",
            cellRenderer: TemplateNameCell,
            ...AgGridFilter.textNoBlankOptions,
        },
        {
            headerName: "Last Edited",
            field: "edited_at",
            width: 175,
            valueFormatter: ({value}) => {
                if (typeof value !== "number") {
                    return "";
                }
                return dateFormatterNoTime(value);
            },
            ...AgGridFilter.date,
        },
        {
            headerName: "Type",
            field: "form_type",
            width: 110,
            sortable: false,
            cellRenderer: TemplateTypeCell,
            valueFormatter: ({value, data: form}) => {
                if (typeof value !== "number") {
                    return "";
                }
                return form.schedule_only ? "Schedule Only" : FormType.mapToName(form.form_type);
            },
        },
        {
            headerName: "Access",
            field: "share_teams_count",
            width: 90,
            sortable: false,
            cellRenderer: TemplateTeamsCell,
            valueFormatter: ({value, data: form}) => {
                if (typeof value !== "number") {
                    return "";
                }
                /** @type {string[]} */
                const teamNames = form.teams.map(team => team.name);
                return teamNames.sort().join(", ");
            },
        },
        {
            headerName: "Submissions",
            field: "submissions_count",
            width: 140,
        },
        {
            headerName: "Scheduled",
            field: "schedulers_count",
            width: 115,
            cellRenderer: TemplateScheduleCell,
            valueFormatter: ({value, data: form}) => {
                if (typeof value !== "number") {
                    return "";
                }
                return form.schedulers_count > 0 ? "Yes" : "No";
            },
        },
        categories.length > 0 && {
            headerName: "Category",
            field: "category.name",
            width: 160,
            ...AgGridFilter.textNullable,
        },
        {
            headerName: "Last Edited By",
            field: "edited_by",
            width: 170,
            sortable: false,
            cellRenderer: SingleAvatarCell,
            valueFormatter: ({value: user}) => formatName(user, {defaultName: ""}),
        },
    ];

    return defs.filter(Boolean);
}

/**
 * @param {URLSearchParams} queryParams 
 * @returns {Promise<{forms: any[], totalForms: number}>}
 */
async function _loadTemplates(queryParams) {
    const response = await get_forms(`?${queryParams}`);
    const content = await response.json();
    const forms = content.results ?? [];
    const totalForms = content.total ?? 0;

    return { forms, totalForms };
}

export const loadTemplates = memoize(_loadTemplates, (queryParams) => queryParams.toString());

/**
 * 
 * @param {any} form 
 * @param {string} orgId 
 * @param {number} pageType
 */
export function getTemplateAnalyticsLink(form, orgId, pageType) {
    return `/${orgId}/home/${getManageFormType(pageType)}/${form.form_uuid}/analytics`;
}

function getManageFormType(pageType) {
    if (pageType === FormType.INCIDENT) {
        return "manage_incident_templates";
    } else if (pageType === FormType.LMS) {
        return "manage_courses";
    } else {
        return "manage_forms_templates";
    }
} 